import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { fmtMoney as _fmtMoney, fmtAsset as _fmtAsset } from "@/lib/api";

const STORAGE_KEY = "demo_mode";

function load() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); }
  catch { return {}; }
}

const DemoContext = createContext(null);

export function DemoProvider({ children }) {
  const [demoActive, setDemoActive] = useState(() => load().active ?? false);
  const [divisor, setDivisorState] = useState(() => Math.max(2, Number(load().divisor) || 10));

  const persist = (active, div) =>
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ active, divisor: div }));

  // Sync body attribute for CSS blur rule
  useEffect(() => {
    if (demoActive) {
      document.body.setAttribute("data-demo-active", "1");
    } else {
      document.body.removeAttribute("data-demo-active");
    }
  }, [demoActive]);

  const toggleDemo = useCallback(() => {
    setDemoActive((prev) => {
      persist(!prev, divisor);
      return !prev;
    });
  }, [divisor]);

  const setDivisor = useCallback((val) => {
    const num = Math.max(2, Number(val) || 2);
    setDivisorState(num);
    persist(demoActive, num);
  }, [demoActive]);

  const fmtMoney = useCallback(
    (val, withSign) => _fmtMoney(demoActive ? val / divisor : val, withSign),
    [demoActive, divisor],
  );

  const fmtAsset = useCallback(
    (val) => _fmtAsset(demoActive ? val / divisor : val),
    [demoActive, divisor],
  );

  return (
    <DemoContext.Provider value={{ demoActive, divisor, setDivisor, toggleDemo, fmtMoney, fmtAsset }}>
      {children}
    </DemoContext.Provider>
  );
}

export const useDemo = () => useContext(DemoContext);
